import { useEffect, useState, useCallback } from 'react'

export interface UseUnsavedChangesWarningReturn {
  showCancelDialog: boolean
  isBackButtonNavigation: boolean
  handleCancelClick: (isDirty: boolean) => void
  handleCancelConfirm: (onNavigate: () => void) => void
  handleCancelDismiss: () => void
}

/**
 * Custom hook to warn users about unsaved changes when navigating away
 * Handles both browser back button and cancel button clicks
 *
 * @param isDirty - Whether the form has unsaved changes
 * @returns Object containing dialog state and handlers
 */
export const useUnsavedChangesWarning = (
  isDirty: boolean
): UseUnsavedChangesWarningReturn => {
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [isBackButtonNavigation, setIsBackButtonNavigation] = useState(false)

  // Handle browser beforeunload event (refresh, close tab)
  const handleBeforeUnload = useCallback(
    (e: BeforeUnloadEvent) => {
      if ((window as any)._skipBeforeUnload) return
      if (isDirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    },
    [isDirty]
  )

  // Handle browser back button
  const handlePopState = useCallback(() => {
    if (isDirty) {
      setIsBackButtonNavigation(true)
      setShowCancelDialog(true)
      // Push state back to prevent navigation
      window.history.pushState(null, '', window.location.href)
    }
  }, [isDirty])

  // Set up event listeners and push initial state
  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('popstate', handlePopState)

    // Push a dummy state to enable back button detection
    window.history.pushState(null, '', window.location.href)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('popstate', handlePopState)
    }
  }, [handleBeforeUnload, handlePopState])

  // Handle cancel button click
  const handleCancelClick = useCallback((isDirty: boolean) => {
    if (isDirty) {
      setIsBackButtonNavigation(false)
      setShowCancelDialog(true)
    } else {
      // If not dirty, the caller should handle navigation directly
      // This is intentionally a no-op to allow the caller to control it
    }
  }, [])

  // Handle confirmation to leave page
  const handleCancelConfirm = useCallback(
    (onNavigate: () => void) => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('popstate', handlePopState)
      setShowCancelDialog(false)
      // Execute the navigation callback
      onNavigate()
    },
    [handleBeforeUnload, handlePopState]
  )

  // Handle dismissing the cancel dialog (stay on page)
  const handleCancelDismiss = useCallback(() => {
    if (isBackButtonNavigation) {
      // Re-push state to keep the page in history
      window.history.pushState(null, '', window.location.href)
    }
    setShowCancelDialog(false)
    setIsBackButtonNavigation(false)
  }, [isBackButtonNavigation])

  return {
    showCancelDialog,
    isBackButtonNavigation,
    handleCancelClick,
    handleCancelConfirm,
    handleCancelDismiss,
  }
}
